import type { AuthChangeEvent, Session, SupabaseClient, User } from '@supabase/supabase-js';
import {
  getLegacyAuthCookieDomains,
  isSupabaseAuthCookieName,
} from '@statura/utils/supabase-auth-cookies';
import { getAuthUserWithRetry, isConfirmedAuthSignOut } from '../lib/supabase/auth-user';

export type AuthStateChangeDecision =
  | { kind: 'authenticated'; user: User }
  | { kind: 'signed-out' };

const TRANSIENT_NULL_SESSION_REVERIFY_OPTIONS = { retries: 1, delayMs: 100 } as const;

/**
 * Message / code fragments that identify a PERMANENT, invalid-grant-class
 * refresh rejection — the refresh token is revoked, already-used, expired, or
 * simply gone (e.g. after a server-side session wipe). These are the only
 * failures that justify purging the local session.
 */
const PERMANENT_REFRESH_FAILURE_PATTERNS = [
  'invalid refresh token',
  'invalid_refresh_token',
  'already used',
  'already_used',
  'refresh token not found',
  'refresh_token_not_found',
  'refresh token expired',
  'refresh_token_expired',
  'invalid grant',
  'invalid_grant',
  'session not found',
  'session_not_found',
  'session from refresh token',
  'session expired',
  'session_expired',
  'user from sub claim in jwt does not exist',
  'user not found',
  'user_not_found',
];

/**
 * Classify a `refreshSession()` failure. Conservative by design: only an
 * unambiguous invalid-grant-class rejection is `'permanent'`. Anything that
 * could be a transient blip — network, timeout, 5xx, rate-limit, or an
 * unrecognised shape — is `'transient'` and must NEVER trigger a session purge,
 * so a flaky Auth REST call can't log out a healthy session (the 2026-05-27
 * random-logout class).
 */
export function classifyRefreshFailure(error: unknown): 'permanent' | 'transient' {
  if (!error || typeof error !== 'object') return 'transient';
  const err = error as { message?: unknown; code?: unknown; status?: unknown };
  const message = typeof err.message === 'string' ? err.message.toLowerCase() : '';
  const code = typeof err.code === 'string' ? err.code.toLowerCase() : '';
  const status = typeof err.status === 'number' ? err.status : undefined;

  // Transient signals win first — never purge on these.
  if (
    message.includes('fetch failed') ||
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('timed out') ||
    message.includes('econnreset') ||
    message.includes('etimedout') ||
    message.includes('rate limit') ||
    code.includes('over_request_rate_limit') ||
    status === 429 ||
    (status !== undefined && status >= 500)
  ) {
    return 'transient';
  }

  const haystack = `${message} ${code}`;
  if (PERMANENT_REFRESH_FAILURE_PATTERNS.some((pattern) => haystack.includes(pattern))) {
    return 'permanent';
  }

  // No recognised invalid-grant marker → transient. We deliberately do NOT
  // purge on a bare 400/401/403: an Auth gateway / WAF / rate-limit variant can
  // return those for a HEALTHY session, and purging would log the user out —
  // worse than the loop, which the #621 recovery screen still heals as a
  // graceful fallback for any unrecognised dead-session message.
  return 'transient';
}

/**
 * Expire any well-formed parent-domain (e.g. `.statura.care`) Supabase auth
 * cookies. `signOut({ scope: 'local' })` only clears the host-only cookies the
 * browser client manages, and the public-path middleware only expires
 * *malformed* parent-domain copies — so a well-formed stale parent-domain cookie
 * (BUG-173 class) would otherwise survive and keep the /login redirect loop
 * alive on its still-valid access token. Mirrors the middleware's parent-domain
 * derivation (DNS hosts only; IP literals and localhost are excluded). Reuses the canonical cookie-name
 * predicate so "what is an auth cookie" stays single-sourced. Exported for
 * unit coverage.
 */
export function expireParentDomainAuthCookies(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const legacyDomains = getLegacyAuthCookieDomains(window.location.hostname);
  if (legacyDomains.length === 0) return;
  for (const entry of document.cookie.split(';')) {
    const name = entry.split('=')[0]?.trim() ?? '';
    if (!isSupabaseAuthCookieName(name)) continue;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    for (const domain of legacyDomains) {
      document.cookie = `${name}=; Domain=${domain}; Path=/; Max-Age=0; SameSite=Lax${secure}`;
    }
  }
}

/**
 * Recover a live session when the browser's in-memory access token has gone
 * stale but the refresh token is still valid (2026-06-09 redirect-loop fix).
 *
 * A long-lived dashboard tab whose access token has expired can have
 * `getUser()` resolve null *before* the client auto-refreshes. Treating that
 * null as a logout bounced the user to /login — but the SERVER still held a
 * valid session, so it redirected straight back, producing an infinite
 * /login↔dashboard loop and a `GET /user` storm that froze the tab.
 *
 * Before concluding "signed out", exchange the refresh token for a fresh
 * access token via `refreshSession()`. Only when there is genuinely no
 * recoverable session does this return null.
 *
 * Security: the refreshed session's locally-decoded token is never trusted as
 * proof of identity. After a successful refresh we still re-validate through
 * `getUser()` (Auth REST signature check) before returning the user.
 *
 * Stale-cookie purge (2026-06-23 "Session out of sync" loop): a server-side
 * session wipe revokes the refresh token while the access token can stay valid
 * for up to an hour. The client's `refreshSession()` then fails permanently,
 * but the server keeps 200-ing `getUser()` on the still-valid access token and
 * the /login page redirects straight back — an infinite bounce + `GET /user`
 * storm even though the client's own session is dead. When (and only when) the
 * refresh fails *permanently*, we purge the local session here so the cookie the
 * server was honouring is gone and the next /login load shows the sign-in form.
 * Classification is
 * deliberately conservative (see `classifyRefreshFailure`): network / timeout /
 * 5xx / ambiguous failures are transient and NEVER purge, so a flaky Auth REST
 * blip can't log out a healthy session (the 2026-05-27 random-logout class).
 */
export type AuthActorResolution =
  | { kind: 'authenticated'; user: User }
  | { kind: 'signed-out' }
  | { kind: 'unavailable' };

export async function recoverAuthActorViaRefresh(
  supabase: SupabaseClient,
  isCurrent: () => boolean = () => true,
): Promise<AuthActorResolution> {
  let refreshedSession: Session | null = null;
  let refreshError: unknown = null;
  try {
    const { data, error } = await supabase.auth.refreshSession();
    refreshedSession = data.session ?? null;
    refreshError = error ?? null;
  } catch (caught) {
    refreshError = caught;
  }

  if (!refreshedSession) {
    // A definitively dead refresh token leaves a cookie the server still
    // honours via its valid access token. Purge it so the bounce loop cannot
    // sustain — but ONLY on an unambiguous permanent rejection, never on a
    // transient blip (which would re-introduce the random-logout class). No
    // cookie gate: a permanent rejection means a dead token was present, and the
    // purge is an idempotent no-op otherwise, so the fix can't silently break if
    // the auth cookie is ever not visible to document.cookie.
    if (refreshError && classifyRefreshFailure(refreshError) === 'permanent') {
      if (!isCurrent()) return { kind: 'unavailable' };
      await clearLocalAuthSession(supabase);
      return { kind: 'signed-out' };
    }
    return { kind: 'unavailable' };
  }

  // Re-validate the identity through getUser() (never trust the locally-decoded
  // token as proof). Reuse the established single-retry so a transient
  // post-refresh Auth REST soft-null is not mistaken for a logout (same class
  // as the 2026-05-27 random-logout fix).
  const { data, error } = await getAuthUserWithRetry(
    supabase,
    TRANSIENT_NULL_SESSION_REVERIFY_OPTIONS,
  );
  if (data.user) return { kind: 'authenticated', user: data.user };
  return { kind: isConfirmedAuthSignOut(error) ? 'signed-out' : 'unavailable' };
}

/** Compatibility for existing callers; the guard consumes the detailed result. */
export async function recoverUserViaRefresh(supabase: SupabaseClient): Promise<User | null> {
  const result = await recoverAuthActorViaRefresh(supabase);
  return result.kind === 'authenticated' ? result.user : null;
}

/**
 * Clear the browser's local Supabase session (the `sb-<ref>-auth-token`
 * cookies) so the next page load starts from a clean slate.
 *
 * Recovery-screen escape hatch (2026-06-23): when the loop breaker trips, the
 * usual cause is a *stale or mismatched* auth cookie left over from a previous
 * session — the client cannot reconcile it into a validated user, yet the
 * cookie is still present so /login bounces straight back. A plain reload
 * re-reads the same bad cookie and re-traps the user; only dropping the local
 * session breaks the loop (exactly what an incognito window does for free).
 *
 * Uses `scope: 'local'` so only THIS browser is cleared — we never revoke the
 * user's sessions on their other devices. Best-effort and time-boxed: clearing
 * the cookie is the goal, so the caller's reload/redirect proceeds even if the
 * call is slow or throws.
 */
export async function clearLocalAuthSession(supabase: SupabaseClient): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      supabase.auth.signOut({ scope: 'local' }),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, 1_500);
      }),
    ]);
  } catch {
    // best-effort — fall through to the caller's reload/redirect regardless
  } finally {
    if (timer) clearTimeout(timer);
    // signOut({ scope: 'local' }) only clears the host-only cookies the browser
    // client manages; also expire any well-formed parent-domain copies so a
    // stale `.statura.care` cookie can't keep the /login redirect loop alive.
    expireParentDomainAuthCookies();
  }
}

/**
 * Redirect-loop breaker. The client and server can disagree about session
 * validity (a stale in-memory access token vs. a still-valid server session).
 * When that happens the client bounces to /login and the server bounces it
 * straight back — an infinite loop that freezes the tab and storms GET /user.
 * Cap the bounces so the disagreement can never become an infinite loop.
 *
 * Two design points the first cut got wrong (caught in review 2026-06-09):
 *
 * 1. The count lives in a module-level variable, not only sessionStorage. The
 *    loop is a chain of client-side `router.replace` soft-navigations within a
 *    single JS context, so a module-level counter survives every lap — and it
 *    keeps working when sessionStorage is unavailable (Safari private mode,
 *    sandboxed frames), which is exactly where a storage-only breaker would be
 *    silently defeated and the loop would return. sessionStorage is kept as a
 *    best-effort mirror so a manual hard reload mid-loop re-trips immediately.
 *
 * 2. Only bounces that recur *rapidly* count. A real logout produces a single
 *    bounce and the user then sits on the /login form; the actual loop fires
 *    several bounces per second. If more than the window elapses between
 *    bounces we start a fresh sequence, so a signed-out user clicking back to a
 *    protected route over time can never accumulate a trip and get trapped.
 */
export const MAX_LOGIN_BOUNCES = 3;
const LOGIN_BOUNCE_WINDOW_MS = 10_000;
const LOGIN_BOUNCE_KEY = 'statura.authGuard.loginBounces';

type BounceState = { count: number; firstAt: number };

let memoizedBounce: BounceState | null = null;

function getSessionStore(): Storage | null {
  try {
    const store = (globalThis as { sessionStorage?: Storage }).sessionStorage;
    return store ?? null;
  } catch {
    // Accessing sessionStorage can throw in privacy modes / sandboxed frames.
    return null;
  }
}

function loadBounceState(): BounceState {
  if (memoizedBounce) return memoizedBounce;
  // Seed once from sessionStorage so a hard reload mid-loop re-trips.
  const store = getSessionStore();
  if (store) {
    try {
      const raw = store.getItem(LOGIN_BOUNCE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<BounceState>;
        const count = typeof parsed.count === 'number' && parsed.count > 0 ? parsed.count : 0;
        const firstAt = typeof parsed.firstAt === 'number' ? parsed.firstAt : 0;
        memoizedBounce = { count, firstAt };
        return memoizedBounce;
      }
    } catch {
      // fall through to a fresh state
    }
  }
  memoizedBounce = { count: 0, firstAt: 0 };
  return memoizedBounce;
}

function persistBounceState(state: BounceState): void {
  memoizedBounce = state;
  const store = getSessionStore();
  if (store) {
    try {
      store.setItem(LOGIN_BOUNCE_KEY, JSON.stringify(state));
    } catch {
      // best-effort mirror only — the in-memory counter is authoritative
    }
  }
}

export function readLoginBounceCount(): number {
  const state = loadBounceState();
  if (state.count > 0 && Date.now() - state.firstAt > LOGIN_BOUNCE_WINDOW_MS) {
    // Stale sequence — a slow, user-driven bounce, not the loop. Don't count it.
    return 0;
  }
  return state.count;
}

export function recordLoginBounce(): number {
  const state = loadBounceState();
  const now = Date.now();
  const withinWindow = state.count > 0 && now - state.firstAt <= LOGIN_BOUNCE_WINDOW_MS;
  const next: BounceState = withinWindow
    ? { count: state.count + 1, firstAt: state.firstAt }
    : { count: 1, firstAt: now };
  persistBounceState(next);
  return next.count;
}

export function clearLoginBounces(): void {
  memoizedBounce = { count: 0, firstAt: 0 };
  const store = getSessionStore();
  if (store) {
    try {
      store.removeItem(LOGIN_BOUNCE_KEY);
    } catch {
      // ignore
    }
  }
}

export async function handleAuthStateChange(
  supabase: SupabaseClient,
  event: AuthChangeEvent,
  session: Session | null,
): Promise<AuthStateChangeDecision> {
  if (session?.user) {
    return { kind: 'authenticated', user: session.user };
  }

  if (event === 'SIGNED_OUT') {
    return { kind: 'signed-out' };
  }

  const { data } = await getAuthUserWithRetry(supabase, TRANSIENT_NULL_SESSION_REVERIFY_OPTIONS);

  if (data.user) {
    return { kind: 'authenticated', user: data.user };
  }

  return { kind: 'signed-out' };
}
